import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import Overlay from 'components/Overlay';
import Toast from 'components/Toast';
import { selectOverlay, selectToasts, getDownloadErrorReportsCount } from './selectors';
import { setToastsList, removeToast } from './actions';
import useUnsavedChangesWarning from 'hooks/useUnsavedChangesWarning';

/**
 * Manages the rendering of Notifications [Toast, Overlays]
 */
const ToastManager = ({ overlay, toasts, setToastList, downloadErrorReportsCount }) => {
  const latestToast = toasts && toasts.length ? toasts[toasts.length - 1] : {};
  // using a ref to access the latest toasts in setTimeout callback.
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;
  
  /**
  * Checks if the toast needs to be auto dismissed. 
  * If the toast doesn't have a waitFotAction flag proceed to auto dismiss.
  */
  const checkToAutoDismiss = useCallback(() => {
    if (!(latestToast?.waitForAction && latestToast?.actions && latestToast?.actions?.length > 0)) {
      setTimeout(() => dismissAndUpdateToasts(latestToast), 3000);
    }
  },[dismissAndUpdateToasts, latestToast]);

  useUnsavedChangesWarning(downloadErrorReportsCount);
  
  useEffect(() => {
    checkToAutoDismiss();
  }, [checkToAutoDismiss, toasts]);

  

  /**
   * Dismiss and update Toast state
   * @param {}
   */
  const dismissAndUpdateToasts = useCallback((toast) => {
    const clonedToasts = [...toastsRef.current];
    const toastIndex = clonedToasts.indexOf(toast);
    if (toastIndex === -1) return;
    clonedToasts.splice(toastIndex, 1);
    setToastList(clonedToasts);
  },[setToastList]);

  return (
    <>
      <Overlay show={overlay} />
      <Toast toasts={toasts} afterAction={dismissAndUpdateToasts} />
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  toasts: selectToasts,
  overlay: selectOverlay,
  downloadErrorReportsCount: getDownloadErrorReportsCount,
});

const mapDispatchToProps = (dispatch) => ({
  setToastList: (toasts) => dispatch(setToastsList(toasts)),
  removeToast: (toastId) => dispatch(removeToast(toastId)),
});

ToastManager.propTypes = {
  overlay: PropTypes.bool,
  toasts: PropTypes.array,
  setToastList: PropTypes.func,
  unsavedWarningAlert: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(ToastManager);
